import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { trackConversion } from "@/hooks/useSiteAnalytics";
import { Plus, X, Eye, MousePointerClick, Sparkles, Star, StarOff, FileDown, Video, Upload, TrendingUp, Trash2, Share2 } from "lucide-react";

const CATEGORIES = ["water", "energy", "coffee", "tea", "functional", "protein", "supplements", "sparkling", "juice"];
const VARIANTS = [
  { value: "clear-pet", label: "Clear PET" },
  { value: "clear-glass", label: "Clear Glass" },
  { value: "matte-black", label: "Matte Black" },
];
const CAPS = [
  { value: "gold", label: "Gold Metallic" },
  { value: "black", label: "Matte Black" },
  { value: "black-gold", label: "Black + Gold Ring" },
];
const EMBLEMS = [
  { value: "circle", label: "Concentric Circles" },
  { value: "drop", label: "pH Drop" },
  { value: "ring", label: "Ring + Dot" },
];
const SERVICES_LIST = [
  "Product Development", "Branding", "Packaging",
  "Manufacturing", "Logistics", "Go-To-Market Strategy",
];

const emptyForm = {
  brand_name: "", product_name: "", client_name: "",
  beverage_category: "water", description: "",
  story: "", challenge: "", solution: "",
  services: [], launch_date: "", industry: "",
  featured: true, active: true, display_order: 0,
  variant: "clear-pet", cap: "gold", emblem: "circle",
  label: "ALKALINE WATER", sub_label: "", volume: "16.9 FL OZ · 500 ML",
};

const AdminShowcase = () => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [analytics, setAnalytics] = useState({ totals: {}, rows: [] });
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [videoUploading, setVideoUploading] = useState(false);

  const load = () => {
    api.get("/admin/showcase").then((r) => { setItems(r.data.showcase); setTotals(r.data.totals); }).catch(() => {});
    api.get("/admin/showcase/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (item) => { setForm({ ...emptyForm, ...item, services: item.services || [] }); setEditing(item.id); };
  const close = () => { setEditing(null); setForm(emptyForm); };
  const update = (k, v) => setForm({ ...form, [k]: v });
  const toggleService = (s) => {
    const has = form.services.includes(s);
    update("services", has ? form.services.filter((x) => x !== s) : [...form.services, s]);
  };

  const uploadHeroVideo = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error("Video exceeds 25 MB — keep loops ≤ 6 seconds");
    setVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/uploads/video?purpose=showcase-hero-video", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, hero_video_url: r.data.url, hero_video_upload_id: r.data.id }));
      toast.success("Hero video uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Video upload failed");
    } finally {
      setVideoUploading(false);
    }
  };

  const exportPdf = async (item) => {
    try {
      const r = await api.get(`/admin/showcase/${item.id}/export-pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flowtalis-case-study-${item.slug || item.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      trackConversion("case_study_download", {
        portfolio_slug: item.slug || undefined,
        brand_name: item.brand_name || undefined,
        format: "pdf",
        surface: "admin",
      });
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF export failed");
    }
  };

  const exportSocialCard = async (item) => {
    try {
      const r = await api.get(`/admin/showcase/${item.id}/social-card`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flowtalis-social-${item.slug || item.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Social card downloaded (1080×1350)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Social card export failed");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await api.post("/admin/showcase", form);
        toast.success("Showcase entry created");
      } else {
        await api.patch(`/admin/showcase/${editing}`, form);
        toast.success("Showcase entry updated");
      }
      close(); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.brand_name}?`)) return;
    try { await api.delete(`/admin/showcase/${item.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleFeatured = async (item) => {
    try {
      await api.patch(`/admin/showcase/${item.id}`, { featured: !item.featured });
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Showcase Portfolio</div>
          <h1 className="text-3xl font-bold tracking-tight text-white">Bottle Media Library</h1>
          <p className="text-white/60 mt-2 max-w-2xl">
            Manage featured client brands. Entries appear in the homepage hero and can rotate automatically.
          </p>
        </div>
        <button onClick={openNew} className="ft-btn-gold" data-testid="showcase-new-btn"><Plus size={16}/> New Showcase</button>
      </div>

      <div className="grid md:grid-cols-4 gap-4 mb-6">
        <div className="ft-card"><div className="ft-stat-label">Total Brands</div><div className="ft-stat-value mt-2">{totals.total ?? 0}</div></div>
        <div className="ft-card"><div className="ft-stat-label">Featured</div><div className="ft-stat-value mt-2" data-testid="totals-featured">{totals.featured ?? 0}</div></div>
        <div className="ft-card"><div className="ft-stat-label flex items-center gap-2">Views <Eye size={12} /></div><div className="ft-stat-value mt-2">{totals.total_views ?? 0}</div></div>
        <div className="ft-card"><div className="ft-stat-label flex items-center gap-2">CTA Clicks <MousePointerClick size={12} /></div><div className="ft-stat-value mt-2">{totals.total_cta_clicks ?? 0}</div></div>
      </div>

      {/* Portfolio Analytics — top-performing case studies */}
      {analytics.rows?.length > 0 && (
        <div className="ft-card p-0 overflow-hidden mb-6" data-testid="showcase-analytics">
          <div className="p-5 border-b border-[var(--ft-border)] flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="ft-eyebrow flex items-center gap-2"><TrendingUp size={13} className="text-[var(--ft-gold)]"/> Portfolio Analytics</div>
              <h3 className="mt-2 text-lg font-bold text-white">Which case studies are pulling consultations</h3>
            </div>
            <div className="flex gap-4 text-xs text-white/70">
              <div><span className="uppercase tracking-wider text-[10px] text-white/50 font-semibold">Total Views</span><div className="text-lg font-bold text-white" data-testid="analytics-total-views">{analytics.totals?.views ?? 0}</div></div>
              <div><span className="uppercase tracking-wider text-[10px] text-white/50 font-semibold">Card Clicks</span><div className="text-lg font-bold text-white">{analytics.totals?.clicks ?? 0}</div></div>
              <div><span className="uppercase tracking-wider text-[10px] text-white/50 font-semibold">CTA Clicks</span><div className="text-lg font-bold" style={{color:"var(--ft-gold)"}} data-testid="analytics-total-cta">{analytics.totals?.cta_clicks ?? 0}</div></div>
            </div>
          </div>
          <table className="ft-table" data-testid="showcase-analytics-table">
            <thead><tr><th>Brand</th><th>Category</th><th>Views</th><th>Clicks</th><th>CTA Clicks</th><th>Conv. %</th><th className="w-32">Share</th></tr></thead>
            <tbody>
              {analytics.rows.slice(0, 10).map((r, i) => {
                const max = Math.max(1, ...analytics.rows.map((x) => x.views || 0));
                const pct = Math.round(((r.views || 0) / max) * 100);
                return (
                  <tr key={r.id} data-testid={`analytics-row-${r.slug}`}>
                    <td>
                      <div className="flex items-center gap-2 font-semibold">
                        <span className="text-[10px] font-mono text-[var(--ft-muted)] w-4">{i + 1}</span>
                        {r.brand_name}
                        {r.is_concept && <span className="text-[0.55rem] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[var(--ft-black)] text-[var(--ft-gold)] border border-[var(--ft-gold)]">Concept</span>}
                      </div>
                      <div className="text-xs text-[var(--ft-muted)]">{r.product_name}</div>
                    </td>
                    <td className="capitalize text-xs">{r.beverage_category}</td>
                    <td>{r.views}</td>
                    <td>{r.clicks}</td>
                    <td className="font-semibold" style={{color:"var(--ft-gold)"}}>{r.cta_clicks}</td>
                    <td className="text-xs">{r.conversion_rate}%</td>
                    <td>
                      <div className="h-1.5 w-full rounded-full bg-[var(--ft-border)] overflow-hidden">
                        <div className="h-full" style={{width: `${pct}%`, background: "var(--ft-gold)"}} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="showcase-table">
          <thead><tr><th>Order</th><th>Brand</th><th>Category</th><th>Views</th><th>Modal Opens</th><th>CTA Clicks</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[var(--ft-muted)]">No showcase entries yet.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} data-testid={`showcase-row-${it.id}`}>
                <td className="font-mono text-sm">{it.display_order}</td>
                <td>
                  <div className="font-semibold">{it.brand_name}</div>
                  <div className="text-xs text-[var(--ft-muted)]">{it.product_name}</div>
                </td>
                <td className="capitalize">{it.beverage_category}</td>
                <td>{it.views || 0}</td>
                <td>{it.modal_opens || 0}</td>
                <td>{it.cta_clicks || 0}</td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleFeatured(it)} title="Toggle featured" data-testid={`showcase-featured-${it.id}`}>
                      {it.featured ? <Star size={16} fill="#c8a24a" style={{color: "#c8a24a"}}/> : <StarOff size={16} className="text-[var(--ft-muted)]"/>}
                    </button>
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold ${it.active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {it.active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </td>
                <td className="text-right">
                  <button onClick={() => exportPdf(it)} title="Export branded case study PDF" className="text-sm font-semibold inline-flex items-center gap-1" style={{color: "var(--ft-charcoal)"}} data-testid={`showcase-pdf-${it.id}`}>
                    <FileDown size={14} /> PDF
                  </button>
                  <button onClick={() => exportSocialCard(it)} title="Download 1080×1350 IG share card" className="ml-3 text-sm font-semibold inline-flex items-center gap-1" style={{color: "var(--ft-charcoal)"}} data-testid={`showcase-social-${it.id}`}>
                    <Share2 size={14} /> Social
                  </button>
                  <button onClick={() => openEdit(it)} className="ml-3 text-sm font-semibold" style={{color: "var(--ft-gold)"}} data-testid={`showcase-edit-${it.id}`}>Edit</button>
                  <button onClick={() => remove(it)} className="ml-3 text-sm text-red-600" data-testid={`showcase-delete-${it.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={close}>
          <div className="bg-white rounded-2xl max-w-2xl w-full my-8 p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-2xl font-bold">{editing === "new" ? "New Showcase Entry" : "Edit Showcase Entry"}</h2>
              <button onClick={close}><X size={20}/></button>
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="showcase-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="ft-label">Brand Name*</label><input required className="ft-input" value={form.brand_name} onChange={(e) => update("brand_name", e.target.value)} data-testid="show-brand-name"/></div>
                <div><label className="ft-label">Product Name*</label><input required className="ft-input" value={form.product_name} onChange={(e) => update("product_name", e.target.value)}/></div>
                <div><label className="ft-label">Client Name</label><input className="ft-input" value={form.client_name} onChange={(e) => update("client_name", e.target.value)}/></div>
                <div><label className="ft-label">Industry</label><input className="ft-input" value={form.industry} onChange={(e) => update("industry", e.target.value)}/></div>
                <div><label className="ft-label">Beverage Category*</label><select className="ft-input" value={form.beverage_category} onChange={(e) => update("beverage_category", e.target.value)}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
                <div><label className="ft-label">Launch Date</label><input type="date" className="ft-input" value={form.launch_date || ""} onChange={(e) => update("launch_date", e.target.value)}/></div>
              </div>
              <div><label className="ft-label">Description*</label><textarea required rows={2} className="ft-input" value={form.description} onChange={(e) => update("description", e.target.value)}/></div>
              <div><label className="ft-label">Brand Story</label><textarea rows={3} className="ft-input" value={form.story} onChange={(e) => update("story", e.target.value)}/></div>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="ft-label">Challenge</label><textarea rows={2} className="ft-input" value={form.challenge} onChange={(e) => update("challenge", e.target.value)}/></div>
                <div><label className="ft-label">Solution</label><textarea rows={2} className="ft-input" value={form.solution} onChange={(e) => update("solution", e.target.value)}/></div>
              </div>

              <div>
                <label className="ft-label">Services Provided</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICES_LIST.map((s) => (
                    <button type="button" key={s} onClick={() => toggleService(s)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.services.includes(s) ? "bg-[var(--ft-black)] text-white border-[var(--ft-black)]" : "border-[var(--ft-border)] hover:border-[var(--ft-gold)]"}`}>
                      <Sparkles size={11} className="inline mr-1"/> {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-[var(--ft-border)]">
                <div className="ft-eyebrow mb-3">Image URLs (paste public URL after uploading via Artwork Center)</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><label className="ft-label">Primary Bottle Image URL</label><input className="ft-input" value={form.bottle_image_url || ""} onChange={(e) => update("bottle_image_url", e.target.value)} placeholder="https://…" data-testid="show-bottle-url"/></div>
                  <div><label className="ft-label">Hero Image URL</label><input className="ft-input" value={form.hero_image_url || ""} onChange={(e) => update("hero_image_url", e.target.value)} placeholder="https://…"/></div>
                  <div><label className="ft-label">Logo URL</label><input className="ft-input" value={form.logo_url || ""} onChange={(e) => update("logo_url", e.target.value)} placeholder="https://…"/></div>
                  <div><label className="ft-label">Lifestyle Image URL</label><input className="ft-input" value={form.lifestyle_image_url || ""} onChange={(e) => update("lifestyle_image_url", e.target.value)} placeholder="https://…"/></div>
                  <div className="md:col-span-2"><label className="ft-label">Alt Text</label><input className="ft-input" value={form.alt_text || ""} onChange={(e) => update("alt_text", e.target.value)} placeholder="Descriptive alt text for accessibility"/></div>
                  <div className="md:col-span-2">
                    <label className="ft-label flex items-center gap-2"><Video size={13} className="text-[var(--ft-gold)]"/> Hero Loop Video (optional — 6s, ≤ 25 MB, MP4/WebM)</label>
                    <div className="flex gap-2">
                      <input className="ft-input flex-1" value={form.hero_video_url || ""} onChange={(e) => update("hero_video_url", e.target.value)} placeholder="/api/uploads/… or https://…" data-testid="show-video-url"/>
                      <label className="ft-btn-ghost cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="show-video-upload">
                        <Upload size={13}/><span className="text-xs">{videoUploading ? "Uploading…" : "Upload"}</span>
                        <input type="file" className="hidden" accept="video/mp4,video/webm,video/quicktime" disabled={videoUploading}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadHeroVideo(f); e.target.value = ""; }} />
                      </label>
                      {form.hero_video_url && (
                        <button type="button" onClick={() => update("hero_video_url", "")} className="ft-btn-ghost inline-flex items-center gap-1 text-red-600" title="Remove video"><Trash2 size={13}/></button>
                      )}
                    </div>
                    {form.hero_video_url && (
                      <div className="mt-2 rounded-lg overflow-hidden border border-[var(--ft-border)] bg-black aspect-video max-w-md" data-testid="show-video-preview">
                        <video src={form.hero_video_url} autoPlay muted loop playsInline className="w-full h-full object-cover"/>
                      </div>
                    )}
                    <div className="text-xs text-[var(--ft-muted)] mt-1">Displayed as a silent autoplay loop on the case-study hero. Keep it under 6 seconds for the best mobile experience.</div>
                  </div>
                  <div><label className="ft-label">Visibility</label>
                    <select className="ft-input" value={form.visibility || "public"} onChange={(e) => update("visibility", e.target.value)}>
                      <option value="draft">Draft</option><option value="private">Private</option><option value="public">Public</option><option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-[var(--ft-border)]">
                <div className="ft-eyebrow mb-3">Bottle Design (SVG fallback if no image uploaded)</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div><label className="ft-label">Variant</label><select className="ft-input" value={form.variant} onChange={(e) => update("variant", e.target.value)}>{VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
                  <div><label className="ft-label">Cap</label><select className="ft-input" value={form.cap} onChange={(e) => update("cap", e.target.value)}>{CAPS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
                  <div><label className="ft-label">Emblem</label><select className="ft-input" value={form.emblem} onChange={(e) => update("emblem", e.target.value)}>{EMBLEMS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}</select></div>
                  <div><label className="ft-label">Label</label><input className="ft-input" value={form.label} onChange={(e) => update("label", e.target.value)}/></div>
                  <div><label className="ft-label">Sub Label</label><input className="ft-input" value={form.sub_label} onChange={(e) => update("sub_label", e.target.value)}/></div>
                  <div><label className="ft-label">Volume</label><input className="ft-input" value={form.volume} onChange={(e) => update("volume", e.target.value)}/></div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4 items-end">
                <div><label className="ft-label">Display Order</label><input type="number" className="ft-input" value={form.display_order} onChange={(e) => update("display_order", Number(e.target.value))}/></div>
                <label className="flex items-center gap-2 pt-6"><input type="checkbox" checked={form.featured} onChange={(e) => update("featured", e.target.checked)}/> Featured on homepage</label>
                <label className="flex items-center gap-2 pt-6"><input type="checkbox" checked={form.active} onChange={(e) => update("active", e.target.checked)}/> Active</label>
              </div>

              <button type="submit" className="ft-btn-gold w-full justify-center" data-testid="showcase-save">Save Showcase Entry</button>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminShowcase;
